import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import { LOGIN_HERO } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Building2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#001D33]">
      <div className="relative hidden lg:block overflow-hidden">
        <img
          src={LOGIN_HERO}
          alt="Desarrollo inmobiliario"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ animation: "kenburns 22s ease-in-out infinite alternate" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#001D33] via-[#002B49]/50 to-transparent" />
        <div className="absolute bottom-0 left-0 p-12 text-white">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="text-xs tracking-[0.3em] uppercase text-[#00A3B5] font-bold mb-3">Gestión inmobiliaria integral</motion.p>
          <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
            className="font-display text-4xl font-bold leading-tight max-w-md">
            Si no vemos,<br />no controlamos.
          </motion.h2>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
            className="mt-4 text-white/60 max-w-sm text-sm leading-relaxed">
            Toda la gestión de tus desarrollos inmobiliarios en un solo lugar: trámites y permisos, coordinación, producto, financiamiento, evidencias y alertas con trazabilidad auditable.
          </motion.p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-11 h-11 rounded-md bg-[#00A3B5] flex items-center justify-center">
              <Building2 className="text-white" size={22} />
            </div>
            <div>
              <p className="font-display font-bold text-white text-xl leading-none">Developware</p>
              <p className="text-white/50 text-xs mt-1">Gestión inmobiliaria integral</p>
            </div>
          </div>
          <h1 className="font-display text-3xl font-bold text-white mb-2">Iniciar sesión</h1>
          <p className="text-white/50 text-sm mb-8">Accede al espacio de trabajo de tu empresa.</p>
          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/70">Correo electrónico</Label>
              <Input id="email" data-testid="login-email-input" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="tu@empresa.com"
                className="bg-white/5 border-white/15 text-white placeholder:text-white/30 h-12" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/70">Contraseña</Label>
              <Input id="password" data-testid="login-password-input" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                className="bg-white/5 border-white/15 text-white placeholder:text-white/30 h-12" />
            </div>
            {error && <p data-testid="login-error" className="text-red-400 text-sm">{error}</p>}
            <Button data-testid="login-submit-button" type="submit" disabled={loading}
              className="w-full h-12 bg-[#00A3B5] hover:bg-[#008C9C] text-white font-bold text-base active:scale-[0.98] transition-transform">
              {loading ? "Verificando..." : "Entrar al sistema"}
            </Button>
          </form>
          <p className="mt-8 text-sm text-white/50 text-center">
            ¿Tu empresa aún no tiene cuenta?{" "}
            <Link data-testid="link-registro" to="/registro" className="text-[#00A3B5] hover:underline font-semibold">Regístrala gratis</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
